import React from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
} from "components/mui";
import { formatScheduleDate } from "utils/canmail/formatScheduleDate";

/**
 * The sticky action panel: template actions on the left, send/schedule actions on the
 * right, and the schedule status line underneath.
 */
function ActionBar({
  isMobile,
  editMode,
  scheduledDate,
  selectedTo,
  recipientMode = "list",
  subject,

  loadedTemplateId,
  hasUneditableDropContent,
  onUpdateTemplateClick,
  onSaveTemplateClick,

  onManageAttachmentsClick,
  onTestSendClick,
  onScheduleClick,
  onCancelEditsClick,
  onSendNowClick,
  onSendClick,
  onClearSchedule,
}) {
  return (
    <Paper
      elevation={4}
      sx={{
        position: { xs: "static", sm: "sticky" },
        bottom: { xs: "auto", sm: 0 },
        zIndex: { xs: "auto", sm: 1000 },
        borderRadius: 2,
        py: 2,
        px: 3,
        mt: 3,
      }}
    >
      {/* Action buttons */}
      <Grid
        container
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        {/* Left-aligned: Template buttons */}
        <Grid item xs={12} sm="auto">
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {/* Not gated on isDropEditor — templates are supported for both editor
                types end to end (backend `editor_type`, SaveTemplateModal and
                LoadTemplateModal all branch on it). */}
            <Button
              variant="outlined"
              color="primary"
              onClick={onUpdateTemplateClick}
              disabled={!loadedTemplateId || hasUneditableDropContent}
              fullWidth={isMobile}
            >
              Update Template
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={onSaveTemplateClick}
              disabled={hasUneditableDropContent}
              fullWidth={isMobile}
            >
              Save as Template
            </Button>
          </Box>
        </Grid>

        {/* Right-aligned: Action buttons */}
        <Grid item xs={12} sm="auto">
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button variant="contained" onClick={onManageAttachmentsClick} fullWidth={isMobile}>
              {editMode ? "Manage Attachments" : "Add Attachments"}
            </Button>

            {!editMode && (
              <Button
                variant="outlined"
                color="primary"
                onClick={onTestSendClick}
                fullWidth={isMobile}
              >
                Test Send
              </Button>
            )}

            <Button
              variant="outlined"
              color="primary"
              onClick={onScheduleClick}
              disabled={recipientMode === "adhoc" || !selectedTo || !subject.trim()}
              fullWidth={isMobile}
            >
              Schedule
            </Button>

            {editMode && (
              <Button
                variant="outlined"
                color="error"
                onClick={onCancelEditsClick}
                fullWidth={isMobile}
              >
                Cancel Edits
              </Button>
            )}

            {editMode && (
              <Button
                variant="outlined"
                color="warning"
                onClick={onSendNowClick}
                fullWidth={isMobile}
              >
                Send Now
              </Button>
            )}

            <Button
              variant="contained"
              color="secondary"
              onClick={onSendClick}
              fullWidth={isMobile}
            >
              {editMode ? "Update" : "Send"}
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Status message */}
      <Box sx={{ mt: 2, textAlign: { xs: "center", sm: "right" } }}>
        <Typography variant="body2" color="text.secondary">
          {scheduledDate
            ? `This email will be sent on ${formatScheduleDate(scheduledDate)}`
            : "This email will be sent immediately"}
        </Typography>
        {scheduledDate && !editMode && (
          <Button size="small" color="error" onClick={onClearSchedule} sx={{ mt: 1 }}>
            Clear Schedule
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export default ActionBar;
